import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Check, X, MapPin, CalendarDays, Send, BadgePercent, LogIn, ShieldCheck, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { discountPrice } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FirmIcon from "../components/FirmIcon";
import StarRating from "../components/StarRating";
import HelpfulButton from "../components/HelpfulButton";
import DiscountBadge from "../components/DiscountBadge";
import PayoutProofCard from "../components/PayoutProof";
import ChallengeTable from "../components/ChallengeTable";
import Reveal, { SectionLabel } from "../components/Reveal";

function ReviewForm({ firmId, onPosted }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ rating: 5, title: "", comment: "" });
  const [busy, setBusy] = useState(false);

  const inputCls = "mt-2 w-full rounded-xl border border-line bg-ink px-3.5 py-2.5 text-sm text-white outline-none transition-colors focus:border-signal";

  if (user === null)
    return <div className="card-violet p-8 font-mono text-xs uppercase tracking-[0.25em] text-muted">CHECKING MEMBERSHIP…</div>;

  if (user === false)
    return (
      <div className="card-violet p-8 text-center" data-testid="review-signin-prompt">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// File Your Report"}</p>
        <h3 className="mt-2 font-display text-2xl font-800 tracking-tight text-white">Members review, the club trusts</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted">
          Reviews on Funded Traders Club are linked to real member profiles — no anonymous noise.
          Sign in free with Google or email to share yours.
        </p>
        <button onClick={() => navigate("/auth")} data-testid="review-signin-btn"
          className="btn-beam btn-gradient mt-6 inline-flex items-center gap-2 px-7 py-3.5 font-display text-sm font-800 text-white">
          <LogIn size={14} /> Sign In To Review
        </button>
      </div>
    );

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/firms/${firmId}/reviews`, form);
      toast.success("REVIEW LOGGED — THANK YOU, TRADER");
      setForm({ rating: 5, title: "", comment: "" });
      onPosted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-violet p-6 sm:p-8" data-testid="review-form">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// File Your Report"}</p>
          <h3 className="mt-2 font-display text-2xl font-800 tracking-tight text-white">Rate this firm</h3>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-signal/40 bg-signal/10 px-3.5 py-1.5 font-mono text-[10px] text-white" data-testid="review-posting-as">
          Posting as <span className="font-700 text-signal">{user.name}</span>
        </span>
      </div>
      <div className="mt-6">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Your Rating</label>
        <div className="mt-3">
          <StarRating idPrefix="review" value={form.rating} size={22} onChange={(r) => setForm({ ...form, rating: r })} />
        </div>
      </div>
      <div className="mt-5">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Headline</label>
        <input maxLength={120} data-testid="review-title-input"
          value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className={inputCls} placeholder="One line verdict" />
      </div>
      <div className="mt-5">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Full Review</label>
        <textarea required minLength={5} maxLength={2000} rows={4} data-testid="review-comment-input"
          value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })}
          className={`${inputCls} resize-none leading-relaxed`}
          placeholder="Payouts, rules, support, execution — tell the club what happened." />
      </div>
      <button type="submit" disabled={busy} data-testid="review-submit-btn"
        className="btn-beam btn-gradient mt-6 inline-flex items-center gap-2 px-7 py-3.5 font-display text-sm font-800 text-white transition-opacity disabled:opacity-50">
        <Send size={13} /> {busy ? "Transmitting…" : "Submit Review"}
      </button>
    </form>
  );
}

export default function FirmDetail() {
  const { slug } = useParams();
  const [firm, setFirm] = useState(null);
  const [reviews, setReviews] = useState([]);

  const updateHelpful = (id, count, voted) =>
    setReviews((rs) => rs.map((r) => (r.id === id ? { ...r, helpful_count: count, helpful_voted: voted } : r)));
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    api.get(`/firms/${slug}`)
      .then(({ data }) => {
        setFirm(data);
        return api.get(`/firms/${data.id}/reviews`);
      })
      .then(({ data }) => setReviews(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => { setFirm(null); setNotFound(false); load(); }, [load]);

  if (notFound)
    return (
      <div>
        <Header />
        <div className="mx-auto max-w-7xl px-5 py-32 text-center font-mono text-sm uppercase tracking-[0.25em] text-muted" data-testid="firm-not-found">
          404 — FIRM NOT IN THE CLUB
        </div>
        <Footer />
      </div>
    );

  if (!firm)
    return (
      <div>
        <Header />
        <div className="mx-auto max-w-7xl px-5 py-32 font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="firm-loading">
          PULLING FILE<span className="animate-blink">…</span>
        </div>
      </div>
    );

  const specs = [
    { l: "Entry From", v: firm.min_price },
    { l: "Max Capital", v: firm.max_capital },
    { l: "Profit Split", v: firm.profit_split },
    { l: "Payout Cycle", v: firm.payout_frequency },
    { l: "Total Paid Out", v: firm.payouts_paid },
    { l: "Traders Funded", v: firm.traders_count },
  ];

  return (
    <div data-testid="firm-detail-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="terminal-grid absolute inset-0 opacity-50" />
        <div className="absolute -right-24 top-0 h-80 w-80 rounded-full bg-signal/15 blur-[130px]" />
        <div className="absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-signal/10 blur-[120px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <Link to="/firms" data-testid="back-to-firms" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted transition-colors hover:text-signal">
            <ArrowLeft size={12} /> Back to Index
          </Link>
          <div className="mt-8 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <div className="flex items-center gap-5">
                <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border border-line bg-elevate">
                  <FirmIcon name={firm.icon} size={34} logo={firm.logo} />
                </div>
                <div>
                  <h1 className="font-display text-4xl font-900 tracking-tight text-white sm:text-6xl" data-testid="firm-name">{firm.name}</h1>
                  <p className="mt-2 flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
                    <span className="rounded-full border border-line bg-surface px-3 py-1 text-signal">{firm.category || "CFD"}</span>
                    {firm.pinned && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-3 py-1 text-gold" data-testid="highest-rated-badge">
                        <Crown size={11} /> Highest Rated
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5"><MapPin size={11} /> {firm.headquarters}</span>
                    <span className="inline-flex items-center gap-1.5"><CalendarDays size={11} /> EST. {firm.founded}</span>
                    {firm.prices_verified && (
                      <span className="pulse-verified inline-flex items-center gap-1.5 rounded-full border border-profit/40 bg-profit/10 px-3 py-1 text-profit" data-testid="prices-verified-badge">
                        <ShieldCheck size={11} /> Prices verified {firm.prices_verified}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted">{firm.tagline}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="flex flex-wrap items-center gap-5">
              <div className="rounded-2xl border border-signal/40 bg-signal/5 px-7 py-5 text-center shadow-[0_0_40px_rgba(0,212,255,0.2)]" data-testid="editorial-score">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-signal">FTC Score</p>
                <p className="mt-1 font-mono text-5xl font-700 text-signal text-glow-green">{firm.rating.toFixed(1)}</p>
                <div className="mt-2 flex justify-center"><StarRating value={firm.rating} idPrefix="editorial" /></div>
              </div>
              <a href={firm.website_url} target="_blank" rel="noopener noreferrer sponsored" data-testid="visit-firm-btn"
                className="btn-beam btn-gradient inline-flex items-center gap-2 px-7 py-4 font-display text-sm font-800 text-white">
                Visit Firm <ExternalLink size={13} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-[1fr_360px]">
          <div>
            {firm.discount_code && (
              <Reveal>
                <div className="mb-10 flex flex-wrap items-center justify-between gap-5 rounded-2xl border border-signal/30 bg-signal/[0.06] p-6 sm:p-7" data-testid="discount-box">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-signal/15">
                      <BadgePercent size={22} className="text-signal" />
                    </div>
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-signal">Club Member Offer</p>
                      <p className="mt-1 font-display text-lg font-800 text-white">{firm.discount_label} with code {firm.discount_code}</p>
                    </div>
                  </div>
                  <DiscountBadge label={firm.discount_label} code={firm.discount_code} slug={`detail-${firm.slug}`} />
                </div>
              </Reveal>
            )}

            <Reveal>
              <SectionLabel>The File</SectionLabel>
              <p className="mt-4 text-base leading-relaxed text-muted">{firm.description}</p>
            </Reveal>

            <Reveal delay={0.05}>
              <div className="mt-10 grid grid-cols-2 gap-2.5 sm:grid-cols-3" data-testid="firm-specs">
                {specs.map((s) => (
                  <div key={s.l} className="card-violet px-5 py-4">
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">{s.l}</p>
                    <p className="mt-1.5 font-mono text-sm font-600 text-signal">{s.v || "—"}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            {firm.challenges && firm.challenges.length > 0 && (
              <Reveal delay={0.07}>
                <ChallengeTable firm={firm} />
              </Reveal>
            )}

            <Reveal delay={0.08}>
              <div className="mt-10 grid gap-4 sm:grid-cols-2" data-testid="pros-cons">
                <div className="card-violet p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Long Signals"}</p>
                  <ul className="mt-5 space-y-3.5">
                    {firm.pros.map((p, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-muted">
                        <Check size={15} className="mt-0.5 shrink-0 text-signal" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="card-violet p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-loss">{"// Short Signals"}</p>
                  <ul className="mt-5 space-y-3.5">
                    {firm.cons.map((c, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-muted">
                        <X size={15} className="mt-0.5 shrink-0 text-loss" /> {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 grid gap-8 sm:grid-cols-2">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Platforms</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {firm.platforms.map((p) => (
                      <span key={p} className="rounded-full border border-line bg-surface px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white">{p}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Instruments</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {firm.instruments.map((p) => (
                      <span key={p} className="rounded-full border border-line bg-surface px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white">{p}</span>
                    ))}
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="mt-16" data-testid="reviews-section">
                <SectionLabel>Member Reports</SectionLabel>
                <h2 className="mt-2 font-display text-3xl font-800 tracking-tight text-white">
                  From the club
                  {firm.user_rating != null && (
                    <span className="ml-4 font-mono text-lg font-600 text-gold">{firm.user_rating.toFixed(1)} avg · {firm.review_count} reports</span>
                  )}
                </h2>
                <div className="mt-8 space-y-3.5">
                  {reviews.length === 0 && (
                    <p className="card-violet p-7 font-mono text-xs uppercase tracking-[0.2em] text-muted" data-testid="reviews-empty">
                      No reports filed yet — be the first.
                    </p>
                  )}
                  {reviews.map((r) => (
                    <article key={r.id} className="card-violet p-7" data-testid={`review-${r.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {r.picture ? (
                            <img src={r.picture} alt="" className="h-9 w-9 rounded-full border border-line object-cover" />
                          ) : (
                            <div className="flex h-9 w-9 items-center justify-center rounded-full border border-line bg-elevate font-mono text-xs font-700 text-signal">
                              {r.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            {r.user_id ? (
                              <Link to={`/members/${r.user_id}`} className="text-sm font-600 text-white transition-colors hover:text-signal" data-testid={`reviewer-link-${r.id}`}>
                                {r.name}
                              </Link>
                            ) : (
                              <p className="text-sm font-600 text-white">{r.name}</p>
                            )}
                            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted">
                              {new Date(r.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {r.verified_payout && (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-signal/50 bg-signal/10 px-3 py-1 font-mono text-[9px] font-700 uppercase tracking-[0.12em] text-signal" data-testid={`verified-badge-${r.id}`}>
                              <ShieldCheck size={11} /> Verified Payout
                            </span>
                          )}
                          <StarRating value={r.rating} idPrefix={`r-${r.id.slice(0, 6)}`} />
                        </div>
                      </div>
                      {r.title && <h3 className="mt-4 font-display text-lg font-700 tracking-tight text-white">{r.title}</h3>}
                      <p className="mt-2 text-sm leading-relaxed text-muted">{r.comment}</p>
                      <div className="mt-4 flex justify-end border-t border-line/50 pt-4">
                        <HelpfulButton review={r} onUpdate={updateHelpful} />
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </Reveal>

            <div className="mt-10">
              <PayoutProofCard firmId={firm.id} />
            </div>

            <div className="mt-10">
              <ReviewForm firmId={firm.id} onPosted={load} />
            </div>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="card-violet p-7" data-testid="quick-verdict">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Quick Verdict"}</p>
              <p className="mt-4 text-sm leading-relaxed text-muted">
                {firm.rating >= 4.5
                  ? `${firm.name} clears the Funded Traders Club bar. Verified payout history, transparent rules and a score in the top tier of the index.`
                  : firm.rating >= 4.0
                  ? `${firm.name} is a solid desk with minor flags. Read the short signals before committing your evaluation fee.`
                  : `${firm.name} sits below our comfort threshold. Proceed with reduced size or look higher up the index.`}
              </p>
              <a href={firm.website_url} target="_blank" rel="noopener noreferrer sponsored" data-testid="sidebar-visit-btn"
                className="btn-beam btn-gradient mt-6 flex items-center justify-center gap-2 px-5 py-3.5 font-display text-sm font-800 text-white">
                Visit {firm.name} <ExternalLink size={13} />
              </a>
              <p className="mt-4 text-center font-mono text-[9px] uppercase tracking-[0.15em] text-muted">Referral link — scores stay independent</p>
            </div>
            <div className="card-violet p-7">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">{"// Rule Of The Club"}</p>
              <p className="mt-4 font-mono text-sm leading-relaxed text-white">
                "Never risk more than 1% of a funded account on a single idea. The firm is watching the drawdown — so should you."
              </p>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
